import { z } from "zod";
import { businessUnitTypeSchema } from "../auth/features.js";
import { localDateSchema, moneySchema, uuidSchema } from "../common/primitives.js";
import { accountTypeSchema, balanceSideSchema, journalEntryStatusSchema, journalEntryTypeSchema, } from "./accounting.js";
const queryBoolean = z
    .enum(["true", "false"])
    .default("false")
    .transform((value) => value === "true");
/** Tüm raporlar varsayılan olarak yalnızca KESİNLEŞMİŞ fişleri kullanır; taslaklar ayrıca işaretlenerek eklenir. */
const reportBaseShape = {
    companyId: uuidSchema,
    includeDrafts: queryBoolean.describe("Taslak fişleri de dâhil et (rapor üzerinde ayrıca belirtilir)"),
};
const dimensionFilterShape = {
    stationId: uuidSchema.optional(),
    businessUnit: businessUnitTypeSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    costCenterId: uuidSchema.optional(),
    instrumentId: uuidSchema.optional(),
};
const rangeShape = { dateFrom: localDateSchema, dateTo: localDateSchema };
const validRange = (value) => value.dateFrom <= value.dateTo;
const rangeError = { message: "Başlangıç tarihi bitiş tarihinden sonra olamaz", path: ["dateTo"] };
// ── Mizan ────────────────────────────────────────────────────────────────────
export const trialBalanceQuerySchema = z
    .object({
    ...reportBaseShape,
    ...rangeShape,
    ...dimensionFilterShape,
    level: z.coerce
        .number()
        .int()
        .min(1)
        .max(7)
        .default(3)
        .describe("Hesap seviyesi: 1 sınıf, 2 grup, 3 ana hesap, 4+ alt hesap"),
    hideZero: queryBoolean.describe("Hareketsiz ve bakiyesiz hesapları gizle"),
})
    .refine(validRange, rangeError);
export const trialBalanceRowSchema = z.object({
    accountId: uuidSchema,
    code: z.string(),
    name: z.string(),
    level: z.number().int(),
    accountType: accountTypeSchema,
    openingDebit: moneySchema.describe("Devir borç toplamı (dateFrom öncesi)"),
    openingCredit: moneySchema,
    periodDebit: moneySchema,
    periodCredit: moneySchema,
    totalDebit: moneySchema,
    totalCredit: moneySchema,
    balanceDebit: moneySchema.describe("Borç bakiyesi (toplam borç > toplam alacak ise fark, değilse 0)"),
    balanceCredit: moneySchema,
});
export const trialBalanceSchema = z.object({
    includesDrafts: z.boolean(),
    rows: z.array(trialBalanceRowSchema),
    totals: trialBalanceRowSchema.omit({
        accountId: true,
        code: true,
        name: true,
        level: true,
        accountType: true,
    }),
});
// ── Muavin defter ────────────────────────────────────────────────────────────
export const accountLedgerQuerySchema = z
    .object({
    ...reportBaseShape,
    ...rangeShape,
    ...dimensionFilterShape,
    accountId: uuidSchema.describe("Üst hesap verilirse alt hesaplarının tümü dâhil edilir"),
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(1000).default(200),
})
    .refine(validRange, rangeError);
export const ledgerLineSchema = z.object({
    lineId: uuidSchema,
    entryId: uuidSchema,
    entryNo: z.string().nullable(),
    entryType: journalEntryTypeSchema,
    entryStatus: journalEntryStatusSchema,
    postingDate: localDateSchema,
    accountCode: z.string(),
    description: z.string(),
    docType: z.string().nullable(),
    docNo: z.string().nullable(),
    source: z.string(),
    sourceDocType: z.string().nullable(),
    sourceDocId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    debit: moneySchema,
    credit: moneySchema,
    runningBalance: moneySchema.describe("Yürüyen bakiye: borç − alacak (negatifse alacak bakiyesi)"),
});
export const accountLedgerSchema = z.object({
    includesDrafts: z.boolean(),
    account: z.object({ id: uuidSchema, code: z.string(), name: z.string(), normalBalance: balanceSideSchema }),
    openingBalance: moneySchema,
    periodDebit: moneySchema,
    periodCredit: moneySchema,
    closingBalance: moneySchema,
    totalLines: z.number().int(),
    lines: z.array(ledgerLineSchema),
});
// ── Yevmiye defteri ──────────────────────────────────────────────────────────
export const journalBookQuerySchema = z
    .object({
    ...reportBaseShape,
    ...rangeShape,
    offset: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(500).default(100).describe("Sayfa başına fiş"),
})
    .refine(validRange, rangeError);
export const journalBookSchema = z.object({
    includesDrafts: z.boolean(),
    totalEntries: z.number().int(),
    totalDebit: moneySchema,
    totalCredit: moneySchema,
    entries: z.array(z.object({
        entryId: uuidSchema,
        entryNo: z.string().nullable(),
        entryType: journalEntryTypeSchema,
        status: journalEntryStatusSchema,
        postingDate: localDateSchema,
        description: z.string(),
        lines: z.array(z.object({
            lineNo: z.number().int(),
            accountCode: z.string(),
            accountName: z.string(),
            description: z.string().nullable(),
            docType: z.string().nullable(),
            docNo: z.string().nullable(),
            docDate: localDateSchema.nullable(),
            debit: moneySchema,
            credit: moneySchema,
        })),
    })),
});
// ── Büyük defter (kebir) ─────────────────────────────────────────────────────
export const generalLedgerQuerySchema = z
    .object({
    ...reportBaseShape,
    ...rangeShape,
    codeFrom: z
        .string()
        .regex(/^\d{1,3}$/)
        .optional()
        .describe("Ana hesap kodu alt sınırı (ör. 100)"),
    codeTo: z
        .string()
        .regex(/^\d{1,3}$/)
        .optional(),
    maxLinesPerAccount: z.coerce.number().int().min(1).max(2000).default(500),
})
    .refine(validRange, rangeError);
export const generalLedgerSchema = z.object({
    includesDrafts: z.boolean(),
    accounts: z.array(z.object({
        code: z.string(),
        name: z.string(),
        openingBalance: moneySchema,
        periodDebit: moneySchema,
        periodCredit: moneySchema,
        closingBalance: moneySchema,
        truncated: z.boolean(),
        lines: z.array(ledgerLineSchema),
    })),
});
// ── Bilanço ve gelir tablosu ─────────────────────────────────────────────────
const statementLineSchema = z.object({
    code: z.string(),
    name: z.string(),
    level: z.number().int(),
    amount: moneySchema,
    compareAmount: moneySchema.nullable(),
});
export const balanceSheetQuerySchema = z.object({
    ...reportBaseShape,
    asOf: localDateSchema,
    compareAsOf: localDateSchema.optional(),
});
export const balanceSheetSchema = z.object({
    includesDrafts: z.boolean(),
    asOf: localDateSchema,
    compareAsOf: localDateSchema.nullable(),
    assets: z.array(statementLineSchema),
    liabilitiesAndEquity: z.array(statementLineSchema),
    totalAssets: moneySchema,
    totalLiabilitiesAndEquity: moneySchema,
    compareTotalAssets: moneySchema.nullable(),
    compareTotalLiabilitiesAndEquity: moneySchema.nullable(),
    periodResult: moneySchema.describe("Henüz kapatılmamış gelir–gider farkı; özkaynaklarda 'Dönem net kârı/zararı' olarak gösterilir"),
});
export const incomeStatementQuerySchema = z
    .object({
    ...reportBaseShape,
    ...rangeShape,
    compareFrom: localDateSchema.optional(),
    compareTo: localDateSchema.optional(),
    stationId: uuidSchema.optional(),
    businessUnit: businessUnitTypeSchema.optional(),
})
    .refine(validRange, rangeError)
    .refine((value) => (value.compareFrom === undefined) === (value.compareTo === undefined), {
    message: "Karşılaştırma dönemi için iki tarih birlikte verilmelidir",
    path: ["compareTo"],
});
export const incomeStatementSchema = z.object({
    includesDrafts: z.boolean(),
    lines: z.array(statementLineSchema.extend({ kind: z.enum(["REVENUE", "EXPENSE", "SUBTOTAL"]) })),
    netResult: moneySchema,
    compareNetResult: moneySchema.nullable(),
});
// ── Boyut bazlı kârlılık ─────────────────────────────────────────────────────
export const dimensionProfitabilityQuerySchema = z
    .object({
    ...reportBaseShape,
    ...rangeShape,
    groupBy: z.enum(["STATION", "BUSINESS_UNIT", "STATION_BUSINESS_UNIT"]).default("STATION"),
})
    .refine(validRange, rangeError);
export const dimensionProfitabilitySchema = z.object({
    includesDrafts: z.boolean(),
    rows: z.array(z.object({
        stationId: uuidSchema.nullable(),
        stationName: z.string().nullable(),
        businessUnit: businessUnitTypeSchema.nullable(),
        revenue: moneySchema,
        expense: moneySchema,
        net: moneySchema,
    })),
    totals: z.object({ revenue: moneySchema, expense: moneySchema, net: moneySchema }),
});
// ── Dış muhasebe programına aktarım ──────────────────────────────────────────
export const ACCOUNTING_EXPORT_ADAPTERS = ["GENERIC", "LOGO", "MIKRO", "LUCA"];
export const ACCOUNTING_EXPORT_FORMATS = ["CSV", "XLSX", "JSON"];
export const accountingExportAdapterListSchema = z.object({
    items: z.array(z.object({
        adapter: z.enum(ACCOUNTING_EXPORT_ADAPTERS),
        name: z.string(),
        formats: z.array(z.enum(ACCOUNTING_EXPORT_FORMATS)),
        available: z.boolean().describe("false: iskelet; biçim teyidi bekliyor"),
        note: z.string().nullable(),
    })),
});
export const accountingExportRequestSchema = z
    .object({
    companyId: uuidSchema,
    adapter: z.enum(ACCOUNTING_EXPORT_ADAPTERS).default("GENERIC"),
    format: z.enum(ACCOUNTING_EXPORT_FORMATS).default("XLSX"),
    ...rangeShape,
})
    .refine(validRange, rangeError);
export const accountingExportResultSchema = z.object({
    fileName: z.string(),
    mimeType: z.string(),
    contentBase64: z.string(),
    entryCount: z.number().int(),
    lineCount: z.number().int(),
});
//# sourceMappingURL=reports.js.map